import { useState, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isMembershipShippingComplete } from '../utils/membershipShipping';

export const useRazorpay = () => {
  const [loading, setLoading] = useState(false);
  const [shippingOpen, setShippingOpen] = useState(false);
  const { user, setUserDirect } = useAuth();
  const pendingMeta = useRef(null);

  const runSubscribe = async (plan, userForPayment) => {
    const u = userForPayment ?? user;
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post('/payments/create-subscription', { plan });
      if (!data.success) throw new Error(data.message);

      const names = { pro: 'Pro — ₹499 + GST/year', business: 'Business — ₹999 + GST/year' };

      const options = {
        key: data.key,
        subscription_id: data.subscriptionId,
        name: 'WebCard Digital',
        description: names[plan] || plan,
        handler: async (response) => {
          try {
            const res = await api.post('/payments/verify', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
              plan,
            });
            if (res.data.success) {
              const updatedUser = { ...u, plan };
              localStorage.setItem('user', JSON.stringify(updatedUser));
              setUserDirect(updatedUser);
              window.location.reload();
            }
          } catch {
            alert('Payment verification failed. Contact support if amount was deducted.');
          }
        },
        prefill: {
          email: u?.email || '',
          contact: u?.phone ? String(u.phone).replace(/\D/g, '') : '',
          name: u?.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : '',
        },
        theme: { color: '#00A7E3' },
        modal: { ondismiss: () => setLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { setLoading(false); alert('Payment failed. Please try again.'); });
      rzp.open();
    } catch (err) {
      console.error('Payment error:', err);
      const code = err.response?.data?.code;
      if (code === 'MEMBERSHIP_SHIPPING_INCOMPLETE') {
        pendingMeta.current = { kind: 'subscribe', plan };
        setShippingOpen(true);
      } else {
        alert(err.response?.data?.message || err.message || 'Failed to initiate payment.');
      }
      setLoading(false);
    }
  };

  const runPayLifetime = async (userForPayment) => {
    const u = userForPayment ?? user;
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post('/payments/create-order', { plan: 'lifetime' });
      if (!data.success) throw new Error(data.message);

      const options = {
        key: data.key,
        amount: data.amount,
        currency: data.currency || 'INR',
        order_id: data.orderId,
        name: 'WebCard Digital',
        description: 'Lifetime Access — ₹2499 + 18% GST (one-time)',
        handler: async (response) => {
          const orderId = response.razorpay_order_id || response.order_id;
          const payId = response.razorpay_payment_id || response.payment_id;
          try {
            const res = await api.post('/payments/verify-order', {
              razorpay_order_id: orderId,
              razorpay_payment_id: payId,
              razorpay_signature: response.razorpay_signature || response.signature,
            });
            if (res.data.success) {
              const updatedUser = { ...u, plan: 'lifetime' };
              localStorage.setItem('user', JSON.stringify(updatedUser));
              setUserDirect(updatedUser);
              window.location.reload();
            }
          } catch (err) {
            const msg =
              err.response?.data?.message ||
              err.message ||
              'Unknown error';
            console.error('verify-order failed:', err.response?.data || err);
            alert(
              `Payment verification failed. ${msg}\n\nIf money was deducted, contact support with your payment ID: ${payId || '—'}`,
            );
            setLoading(false);
          }
        },
        prefill: {
          email: u?.email || '',
          contact: u?.phone ? String(u.phone).replace(/\D/g, '') : '',
          name: u?.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : '',
        },
        theme: { color: '#00A7E3' },
        modal: { ondismiss: () => setLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { setLoading(false); alert('Payment failed. Please try again.'); });
      rzp.open();
    } catch (err) {
      console.error('Lifetime payment error:', err);
      const code = err.response?.data?.code;
      if (code === 'MEMBERSHIP_SHIPPING_INCOMPLETE') {
        pendingMeta.current = { kind: 'lifetime' };
        setShippingOpen(true);
      } else {
        alert(err.response?.data?.message || err.message || 'Failed to start payment.');
      }
      setLoading(false);
    }
  };

  const subscribe = async (plan) => {
    if (loading) return;
    if (!['pro', 'business'].includes(plan)) return;
    if (!isMembershipShippingComplete(user)) {
      pendingMeta.current = { kind: 'subscribe', plan };
      setShippingOpen(true);
      return;
    }
    await runSubscribe(plan, user);
  };

  const payLifetime = async () => {
    if (loading) return;
    if (!isMembershipShippingComplete(user)) {
      pendingMeta.current = { kind: 'lifetime' };
      setShippingOpen(true);
      return;
    }
    await runPayLifetime(user);
  };

  const closeShippingDialog = () => {
    if (loading) return;
    pendingMeta.current = null;
    setShippingOpen(false);
  };

  const onShippingSaved = async (savedUser) => {
    const meta = pendingMeta.current;
    pendingMeta.current = null;
    setShippingOpen(false);
    setUserDirect(savedUser);
    if (!meta) return;
    if (meta.kind === 'subscribe') await runSubscribe(meta.plan, savedUser);
    else await runPayLifetime(savedUser);
  };

  return {
    subscribe,
    payLifetime,
    loading,
    shippingOpen,
    closeShippingDialog,
    onShippingSaved,
  };
};
